import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { useApi } from "~hooks/apiHooks/useApi";
import { useInvalidateCache } from "~hooks/useInvalidateCache.ts";
import { useUpdatePermissionScopeCache } from "~hooks/useUpdatePermissionScopeCache.ts";
import type { UpdateEntityGroup } from "~pages/EntityGroups/types/upsertEntityGroup.ts";
import type { EntityGroup, EntityGroupResponse } from "~types/entityGroup.ts";

export const useUpdateEntityGroup = () => {
  const { sport, organizationId } = useParams();
  const queryClient = useQueryClient();
  const request = useApi();
  const { mutateAsync: invalidateCache } = useInvalidateCache();
  const updatePermissionScopeCache = useUpdatePermissionScopeCache();

  return useMutation<EntityGroup, Error, UpdateEntityGroup>({
    mutationFn: async ({ entityGroupId, ...body }) => {
      const response = await request<EntityGroupResponse>(`${sport}/o/${organizationId}/entityGroups/${entityGroupId}`, {
        method: "PUT",
        body,
      });

      return response?.data?.[0];
    },
    onSuccess: async (updatedEntityGroup, { entityGroupId }) => {
      if (!updatedEntityGroup?.entityGroupId) {
        return;
      }

      updatePermissionScopeCache(sport, organizationId, (updatedScope) => {
        if (updatedScope.resources?.entityGroups?.[entityGroupId] && updatedEntityGroup) {
          updatedScope.resources.entityGroups[entityGroupId] = {
            ...updatedScope.resources.entityGroups[entityGroupId],
            ...updatedEntityGroup,
            entityGroupId,
          };
        }
      });

      await Promise.all([
        invalidateCache(),
        queryClient.invalidateQueries({ queryKey: ["teamGroups", organizationId] }),
        queryClient.invalidateQueries({ queryKey: ["teamGroups", organizationId, entityGroupId] }),
      ]);
    },
  });
};
